import type { AfterViewInit } from '@angular/core';
import { Component, inject, input } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import type { InnogyComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import { DomService, ScriptInjectOn } from '@innogy/utils-dom';
import { Store } from '@ngrx/store';

import { MemberGetMemberService } from '../member-get-member-service/member-get-member.service';
import { getCustomerDetails } from '../member-get-member-state/member-get-member-state.selectors';
import { isPageName, type MGMWidgetConfig } from '../model';

/**
 * The wrapper for the member-get-member (vriendenvoordeel) widget
 */
@Component({
  selector: 'wl-member-get-member-widget',
  templateUrl: './member-get-member-widget.component.html',
})
export class MemberGetMemberWidgetComponent implements AfterViewInit {
  readonly rendering = input<InnogyComponentRendering>();

  readonly config = inject(ENVIRONMENT_CONFIG);
  readonly domService = inject(DomService);
  readonly mgmService = inject(MemberGetMemberService);
  readonly store$ = inject(Store);

  selector = 'mgmco-widget';

  readonly customerDetailsState = this.store$.selectSignal(getCustomerDetails);

  ngAfterViewInit() {
    this.domService.injectScript({
      src: this.config.mgm.embeddedWidgetUrl,
      injectOnPlatform: ScriptInjectOn.CLIENT_ONLY,
      async: true,
      onLoad: () => this.initWidget(),
      onAlreadyLoaded: () => this.initWidget(),
    });
  }

  private initWidget() {
    const widgetConfig: MGMWidgetConfig = {
      displayDefaultIntro: false,
      // NOTE: On 30/11/2022 we received information from an MGMco developer that the
      //       MGM acceptance environment has shut down, only the production environment is running.
      environment: 'production',
      project: this.config.app,
      selector: `#${this.selector}`,
    };

    const pagename = getFieldValue<string>(this.rendering(), 'PageName');
    if (pagename && isPageName(pagename)) {
      widgetConfig.pagename = pagename;
    }

    const customerDetails = this.customerDetailsState();
    if (customerDetails.customerName && customerDetails.customerEmail) {
      // Autofill the customer details obtained during BAC. (dot characters are not allowed currently so have to be removed)
      widgetConfig.userName = customerDetails.customerName.replace(/\./g, '');
      widgetConfig.userEmail = customerDetails.customerEmail;
    }

    this.mgmService.initWidget(widgetConfig);
  }
}
